import { __rest } from 'tslib';
import { t } from '../../i18n.es.js';
import React, { useState, useEffect } from 'react';
import { styled, Tag, SvgIcon, Button, Input, LazyImage, Link } from '@teko/ui';
import { ProductGifts } from '../ProductGifts/ProductGifts.es.js';
import { ProductName } from '../ProductName/ProductName.es.js';
import { FlashSaleBadge } from '../FlashSaleBadge/FlashSaleBadge.es.js';
import { ProductPrice } from '../ProductPrice/ProductPrice.es.js';
import { Loading } from '../Loading/Loading.es.js';

const DisplayStatus = {
    NONE: 1,
    CONTACT: 2,
    BUYABLE: 3,
};
const StyledProductCard = styled.div `
  padding: 0.3rem;
  background: #fff;
  cursor: pointer;
  display: block;
  text-decoration: none;
  color: #333;
  border-radius: 0.5rem;
  height: 100%;
`;
const StyledProductGifts = styled(ProductGifts) `
  margin-top: 0.5rem;
`;
const StyledProductName = styled(ProductName) `
  margin-top: 0.5rem;
`;
const StyledTag = styled(Tag) `
  margin-right: 0.2rem;
  margin-bottom: 0.2rem;
  font-size: 0.65rem;
`;
const ImageWrapper = styled.div `
  position: relative;
`;
const Tags = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
`;
const FrameWrapper = styled.div(({ frame }) => `
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  display: flex;
  flex-wrap: wrap;
  background-image: url(${frame});
  background-size: cover;
  height: 100%;
  width: 100%;
  `);
const PriceWrapper = styled.div `
  display: flex;
  margin-top: 0.5rem;
`;
const Icons = styled.div `
  display: flex;
  flex-wrap: wrap;
  margin-left: 0.2rem;
`;
const StyledIcon = styled(SvgIcon) `
  margin-left: 0.5rem;
`;
const StyledFlashSaleBadge = styled(FlashSaleBadge) `
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: 0;
`;
const Hover = styled.div `
  padding: 0.7rem;
  border-radius: 0.5rem;
  transition: transform 0.2s;
  -webkit-font-smoothing: subpixel-antialiased;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  &:hover {
    transform: scale(1.05);
  }
`;
const CustomLink = styled.a `
  color: unset;
  text-decoration: unset;
`;
const Contact = styled.div(({ theme }) => `
  background-color: ${theme.brand.tertiary};
  color: ${theme.brand.primary};
  padding: 2px 8px;
  font-size: 0.75rem;
  border-radius: 0.25rem;
  width: fit-content;
  font-weight: 500;
  margin-top: 0.5rem;
`);
const AddToCartBtn = styled(Button) `
  font-weight: 500;
`;
const QuantityStepper = styled.div(({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${theme.brand.primary};
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  `);
const StyledInput = styled(Input)(({ theme }) => `
    padding: 0;
    border: none;
    width: 100%;
    text-align: center;
    color: ${theme.brand.primary};
  `);
const StyledButton = styled(Button) `
  border: none;
  height: 40px;
`;
const AddToCartWrapper = styled.div `
  margin-top: 1rem;
`;
const StyledTagStatus = styled(Tag) `
  font-size: 11px;
  border-radius: 4px;
  position: absolute;
  bottom: 0;
  left: 0;
`;
const ProductCard = (_a) => {
    var { tags = [], image, title, price, link, icons = [], coupon, gifts, flashsale, money, regionName, className, totalAvailableByStocks, displayStatus, addToCart, needManageStock, frame, contactLink, tag, showContact = false } = _a, props = __rest(_a, ["tags", "image", "title", "price", "link", "icons", "coupon", "gifts", "flashsale", "money", "regionName", "className", "totalAvailableByStocks", "displayStatus", "addToCart", "needManageStock", "frame", "contactLink", "tag", "showContact"]);
    const [qty, setQty] = useState(() => (addToCart && addToCart.quantityInCart) || 0);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (addToCart && !loading)
            setQty(addToCart.quantityInCart);
    }, [addToCart, loading]);
    const handleUpdateQty = async () => {
        if (qty && qty !== addToCart.quantityInCart) {
            setLoading(true);
            await addToCart.onChangeQuantity(qty);
            setLoading(false);
        }
        else {
            setQty(addToCart.quantityInCart);
        }
    };
    const node = (React.createElement(Hover, { className: "product-card__content" },
        React.createElement("div", null,
            React.createElement(ImageWrapper, null,
                frame && React.createElement(FrameWrapper, { frame: frame }),
                React.createElement(LazyImage, Object.assign({ imageWidth: 320, borderRadius: 8, contain: true }, image, { aspectRatio: 1 })),
                flashsale && (React.createElement(StyledFlashSaleBadge, { size: "md", endAt: flashsale.endAt, sold: flashsale.value, total: flashsale.total, stock: totalAvailableByStocks })),
                !!tags.length && !flashsale && (React.createElement(Tags, null, tags.map((tag, index) => (React.createElement(StyledTag, { key: index, outline: true }, tag))))),
                !flashsale && tag && React.createElement(StyledTagStatus, Object.assign({}, tag))),
            React.createElement("div", { className: "product-card__info" },
                React.createElement(StyledProductName, { needManageStock: needManageStock, totalAvailableByStocks: totalAvailableByStocks, name: title.name, isFlashSelling: flashsale !== null }),
                totalAvailableByStocks > 0 ||
                    displayStatus !== DisplayStatus.CONTACT ? (React.createElement(React.Fragment, null,
                    React.createElement(PriceWrapper, null,
                        React.createElement(ProductPrice, { price: price.price, listedPrice: price.listedPrice, discount: price.discount }),
                        React.createElement(Icons, null, icons.map((icon, index) => (React.createElement(StyledIcon, Object.assign({ key: index }, icon)))))),
                    gifts && (React.createElement(StyledProductGifts, { label: t('QUÀ TẶNG'), images: gifts.images })))) : (React.createElement(React.Fragment, null, displayStatus === DisplayStatus.CONTACT ? (React.createElement(Contact, null, t('LIÊN HỆ'))) : (React.createElement(Tag, { outline: true }, displayStatus)))))),
        addToCart && (React.createElement(AddToCartWrapper, null, !!addToCart.quantityInCart ? (React.createElement(QuantityStepper, null,
            React.createElement(Loading, { isLoading: loading },
                React.createElement(StyledButton, { onClick: (e) => {
                        e.preventDefault();
                        addToCart.onDecrease();
                    }, outline: true, block: true, variant: "primary" },
                    React.createElement(SvgIcon, { name: "subtract", themeColor: "primary", size: 22 })),
                React.createElement(StyledInput, { value: qty || '', onClick: (e) => e.preventDefault(), onChange: (e) => setQty(Number(e.target.value)), type: "number", onBlur: handleUpdateQty, onKeyDown: (e) => {
                        if (e.key === 'Enter')
                            handleUpdateQty();
                    } }),
                React.createElement(StyledButton, { onClick: (e) => {
                        e.preventDefault();
                        addToCart.onIncrease();
                    }, outline: true, block: true, variant: "primary" },
                    React.createElement(SvgIcon, { name: "plus", themeColor: "primary", size: 22 }))))) : (React.createElement(AddToCartBtn, { onClick: (e) => {
                e.preventDefault();
                if (showContact) {
                    if (contactLink)
                        window.open(contactLink);
                    return;
                }
                addToCart.onAddToCart();
            }, size: "lg", disabled: !!(showContact && !contactLink), variant: showContact && !contactLink ? 'disabled' : 'primary', outline: true, block: true }, showContact
            ? contactLink
                ? t('Liên hệ')
                : t('Hết hàng')
            : t('Thêm vào giỏ')))))));
    if (link) {
        return (React.createElement(Link, Object.assign({}, link),
            React.createElement(CustomLink, Object.assign({}, props, { className: className || '' }),
                React.createElement(StyledProductCard, Object.assign({ className: "product-card" }, (regionName && {
                    'data-track-content': true,
                    'data-content-region-name': regionName,
                    'data-content-name': props.sku,
                    'data-content-target': 'productDetail',
                })), node))));
    }
    return (React.createElement(StyledProductCard, Object.assign({}, (regionName && {
        'data-track-content': true,
        'data-content-region-name': regionName,
        'data-content-name': props.sku,
        'data-content-target': 'productDetail',
    }), props, { className: `${className || ''} product-card` }), node));
};

export { DisplayStatus, ProductCard };
